import React, { useContext, useEffect, useRef, useState } from 'react';

import http from 'api/Api';

import { AuthContext } from 'Auth/AuthContext';

import ClientContext from 'Customers/components/detailsModal/ClientContext';

import { PACKAGE_NAMES } from 'Customers/constants';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import { isPolicyDraftValid } from './policyDraftValidation';

import PolicyTextInput from './PolicyTextInput';

import formatRate from './formatRate';

import { labels, pricingLabels } from './translations';

export const buildPolicyDraft = (values) => ({
  activeRate: values.activeRate,
  inactiveRate: values.inactiveRate,
  currency: values.currency,
  ratePeriod: { unit: values.unit, count: Number(values.count) },
  effectiveKind: values.effectiveKind,
  effectiveTerm: values.effectiveTerm,
});
const blank = {
  activeRate: '',
  inactiveRate: '',
  currency: '',
  unit: '',
  count: 1,
  effectiveKind: 'next_uninvoiced_term',
  effectiveTerm: '',
};
const inputRate = (value) =>
  value == null
    ? ''
    : String(value)
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '');
export default function BillingPolicyDraft({ subscriptionId, plan }) {
  const { authState } = useContext(AuthContext) || {};
  const { selected } = useContext(ClientContext) || {};
  const language = authState?.user?.language === 'ar' ? 'ar' : 'en';
  const copy = labels[language];
  const business = pricingLabels[language];
  const setupMessages = business.setup;
  const [access, setAccess] = useState('loading');
  const [context, setContext] = useState(null);
  const [values, setValues] = useState(blank);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [saveUnconfirmed, setSaveUnconfirmed] = useState(false);
  const edited = useRef(false);
  useEffect(() => {
    let mounted = true;
    edited.current = false;
    setValues(blank);
    setContext(null);
    setResult('');
    setError('');
    setAccess('loading');
    setSaveUnconfirmed(false);
    http
      .get(`subscription/${encodeURIComponent(subscriptionId)}/billing-policy/context`)
      .then(({ data }) => {
        if (!mounted) return;
        setContext(data);
        setAccess('allowed');
        const source = data.savedDraft || data.currentPolicy;
        if (!edited.current)
          setValues({
            ...blank,
            activeRate: inputRate(source?.activeRate),
            inactiveRate: inputRate(source?.inactiveRate),
            currency: source?.currency || data.setup?.currency || '',
            unit: source?.ratePeriod?.unit || data.setup?.ratePeriod?.unit || '',
            count: source?.ratePeriod?.count || data.setup?.ratePeriod?.count || 1,
            ...(data.savedDraft
              ? {
                  effectiveKind: data.savedDraft.effectiveKind,
                  effectiveTerm: data.savedDraft.effectiveTerm?.slice(0, 10) || '',
                }
              : {}),
          });
      })
      .catch((e) => {
        if (!mounted) return;
        setAccess(e.response?.status === 403 ? 'denied' : 'error');
        setError(setupMessages[e.response?.data?.code] || copy.unavailable);
      });
    return () => {
      mounted = false;
    };
  }, [subscriptionId]);
  if (access === 'denied') return null;
  if (access === 'loading') return <p role='status'>{copy.loading}</p>;
  const update = (e) => {
    edited.current = true;
    setResult('');
    setError('');
    setValues((v) => ({ ...v, [e.target.name]: e.target.value }));
  };
  const shown = context?.savedDraft || context?.currentPolicy;
  return (
    <details dir={authState?.user?.language === 'ar' ? 'rtl' : 'ltr'} className='mx-4 mt-3 pt-3 border-top'>
      <summary className='fw-bold mb-3'>{business.title}</summary>
      <p className='mb-2'>
        {selected?.name || selected?.subdomain || ''}
        {plan ? ` · ${PACKAGE_NAMES[plan] || plan}` : ''}
      </p>
      <p className='font-size-12 mb-2'>{business.note}</p>
      <div className='pricingLayout'>
        {shown && (
          <section className='p-3 mb-3 rounded bg-light'>
            <h6>{context.savedDraft ? (saveUnconfirmed ? business.previous : business.saved) : business.current}</h6>
            <p className='mb-0'>
              {business.active}: {formatRate(shown.activeRate) ?? '—'} {shown.currency} · {business.inactive}:{' '}
              {formatRate(shown.inactiveRate) ?? '—'} {shown.currency}
              <br />
              {shown.ratePeriod?.count} {copy[shown.ratePeriod?.unit] || shown.ratePeriod?.unit}
              {shown.effectiveTerm && /^\d{4}-\d{2}-\d{2}/.test(shown.effectiveTerm) ? (
                <>
                  <br />
                  {business.date}:{' '}
                  {new Intl.DateTimeFormat(language === 'ar' ? 'ar' : 'en-GB', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    timeZone: 'UTC',
                  }).format(new Date(`${shown.effectiveTerm.slice(0, 10)}T00:00:00Z`))}
                </>
              ) : null}
            </p>
          </section>
        )}
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (busy || !isPolicyDraftValid(values)) {
              setError(copy.invalid);
              return;
            }
            setBusy(true);
            setResult('');
            setError('');
            try {
              await http.post(
                `subscription/${encodeURIComponent(subscriptionId)}/billing-policy`,
                buildPolicyDraft(values)
              );
              setContext((c) => ({ ...c, savedDraft: buildPolicyDraft(values) }));
              setResult(business.success);
              setSaveUnconfirmed(false);
            } catch (failure) {
              setSaveUnconfirmed(true);
              setError(
                setupMessages[failure.response?.data?.code] ||
                  (failure.response?.data?.code === 'BILLING_POLICY_RATE_INVALID'
                    ? copy.rateInvalid
                    : failure.response?.data?.code === 'BILLING_LAUNCH_REQUIRES_EXPLICIT_ZERO'
                    ? copy.explicitZero
                    : business.unknown)
              );
            } finally {
              setBusy(false);
            }
          }}
        >
          <fieldset disabled={busy} className='border-0 p-0'>
            <div className='row'>
              {[
                ['activeRate', copy.activeRate],
                ['inactiveRate', copy.inactiveRate],
                ['currency', copy.currency],
                ['count', business.count],
              ].map(([name, label]) => (
                <div className='col-6 mb-2' key={name}>
                  <PolicyTextInput
                    name={name}
                    label={label}
                    value={values[name]}
                    onChange={update}
                    isRequired
                    type={name === 'count' ? 'number' : 'text'}
                  />
                </div>
              ))}
            </div>
            {[
              [copy.unit, 'unit', ['month', 'year', 'week', 'day']],
              [copy.effect, 'effectiveKind', ['next_uninvoiced_term', 'launch_zero', 'new_contract']],
            ].map(([label, name, options]) => (
              <label className='d-block mb-2' key={name}>
                {label}
                <select className='form-select' name={name} value={values[name]} onChange={update}>
                  <option value='' disabled>
                    {business.select}
                  </option>
                  {options.map((value) => (
                    <option key={value} value={value}>
                      {copy[value] || value}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <div className='mb-2'>
              <PolicyTextInput
                name='effectiveTerm'
                label={copy.term}
                value={values.effectiveTerm}
                onChange={update}
                isRequired
                type='date'
              />
            </div>
            <RoundedButton
              text={business.save}
              color='primary'
              type='submit'
              isDisabled={busy || access !== 'allowed'}
            />
          </fieldset>
          {result && <p role='status'>{result}</p>}
          {error && (
            <p role='alert' className='font-size-14'>
              {saveUnconfirmed && (
                <>
                  <strong>{business.unconfirmed}</strong>
                  <br />
                </>
              )}
              {error}
            </p>
          )}
        </form>
      </div>
    </details>
  );
}
